import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { AnalysisResult, ShelfMapping } from '@/lib/shelfMappingUtils';

export function useImportAnalysis() {
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const analyzeImport = async (importJobId: string): Promise<AnalysisResult | null> => {
    try {
      setAnalyzing(true);
      setError(null);

      const { data, error: fnError } = await supabase.functions.invoke('analyze-goodreads-csv', {
        body: { importJobId }
      });

      if (fnError) {
        throw new Error(fnError.message);
      }

      if (data.error) {
        throw new Error(data.details || data.error);
      }

      setAnalysis(data as AnalysisResult);
      return data as AnalysisResult;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Analysis failed';
      setError(message);
      console.error('Import analysis error:', err);
      return null;
    } finally {
      setAnalyzing(false);
    }
  };

  const saveMappings = async (
    importJobId: string, 
    userId: string,
    mappings: ShelfMapping[]
  ): Promise<boolean> => {
    try {
      // Filter out mappings where nothing will happen (skip with no books)
      const validMappings = mappings.filter(m => m.targetType !== 'skip' || m.bookCount > 0);

      if (validMappings.length === 0) {
        return true; // Nothing to save
      }

      const records = validMappings.map(m => ({
        import_job_id: importJobId,
        user_id: userId,
        goodreads_shelf: m.goodreadsShelf,
        target_type: m.targetType,
        target_value: m.targetType === 'skip' ? null : m.targetValue,
        book_count: m.bookCount,
      }));

      const { error } = await supabase
        .from('import_shelf_mappings')
        .upsert(records, {
          onConflict: 'import_job_id,goodreads_shelf',
        });

      if (error) {
        throw error;
      }

      return true;
    } catch (err) {
      console.error('Error saving mappings:', err);
      setError(err instanceof Error ? err.message : 'Failed to save mappings');
      return false;
    }
  };

  const reset = () => {
    setAnalysis(null);
    setError(null);
    setAnalyzing(false);
  };

  return {
    analyzing,
    analysis,
    error,
    analyzeImport,
    saveMappings,
    reset,
  };
}
